
import { Component, Vue } from "vue-property-decorator";
import DispatcherHubEvent from "@/models/Dispatcher/DispatcherHubEvent";
import IncomingCallEnded from "@/models/Calls/IncomingCallEnded";
import {
  CallsApi,
  Category,
  IncomingCallAnswered,
  IncomingCallStarted,
  RedirectTargetModel,
} from "@/api";
import { getDefaultApiConfig } from "@/utils";
import { eventBus, Events } from "@/utils/event-bus";
import SideAlertModel from "@/models/SideAlertModel";
import CallQueueCard from "@/components/Shared/CallQueueCard.vue";
import CallReceptionStatus from "@/components/Shared/CallReceptionStatus.vue";
import {
  getActiveCall,
  isCallQueueHidden,
  refreshMyActiveCall,
  setActiveCall,
} from "@/store/callReception";

type CallCategory = Category;

interface CategoryMeta {
  value: CallCategory;
  icon: string;
  color: string;
}

type RedirectKind = "dispatcher" | "driver" | "vehicle";

interface RedirectGroupMeta {
  kind: RedirectKind;
  label: string;
  icon: string;
}

interface RedirectGroup extends RedirectGroupMeta {
  targets: RedirectTargetModel[];
}

const REDIRECT_GROUPS: RedirectGroupMeta[] = [
  { kind: "dispatcher", label: "Dispečeři", icon: "mdi-headset" },
  { kind: "driver", label: "Řidiči", icon: "mdi-account" },
  { kind: "vehicle", label: "Vozidla", icon: "mdi-car" },
];

@Component({
  name: "CallQueueBar",
  components: {
    CallQueueCard,
    CallReceptionStatus,
  },
})
export default class CallQueueBar extends Vue {
  get isQueueHidden(): boolean {
    return isCallQueueHidden();
  }

  calls: IncomingCallStarted[] = [];
  isExpanded = false;

  selectedCategory: CallCategory = "Urgent";

  categories: CategoryMeta[] = [
    { value: "Normal", icon: "mdi-account", color: "primary" },
    { value: "Urgent", icon: "mdi-alert", color: "error" },
    { value: "Driver", icon: "mdi-car", color: "success" },
  ];

  get activeCall(): IncomingCallAnswered | null {
    return getActiveCall();
  }

  set activeCall(value: IncomingCallAnswered | null) {
    setActiveCall(value);
  }

  isConnected = false;

  callsApi = new CallsApi(getDefaultApiConfig());

  redirectDialog = false;
  redirectLoading = false;
  redirectSubmitting = false;
  redirectTargets: RedirectTargetModel[] = [];
  private redirectCallTarget:
    | IncomingCallStarted
    | IncomingCallAnswered
    | null = null;

  get redirectGroups(): RedirectGroup[] {
    return REDIRECT_GROUPS.map((group) => ({
      ...group,
      targets: this.redirectTargets.filter(
        (t) => t.kind === group.kind && !!t.phoneNumber
      ),
    })).filter((group) => group.targets.length > 0);
  }

  private seeding = false;
  private pendingEvents: {
    kind: "start" | "end" | "removeFromQueue";
    callId: string;
    call?: IncomingCallStarted;
  }[] = [];

  private static readonly CallStateTtlMs = 60000;
  private callStates = new Map<
    string,
    { queueClosed: boolean; ended: boolean; ts: number }
  >();

  private callState(callId: string): {
    queueClosed: boolean;
    ended: boolean;
    ts: number;
  } {
    const now = Date.now();
    const expired: string[] = [];
    this.callStates.forEach((state, id) => {
      if (now - state.ts > CallQueueBar.CallStateTtlMs) {
        expired.push(id);
      }
    });
    expired.forEach((id) => this.callStates.delete(id));

    let state = this.callStates.get(callId);
    if (!state) {
      state = { queueClosed: false, ended: false, ts: now };
      this.callStates.set(callId, state);
    }
    state.ts = now;
    return state;
  }

  get sortedCalls(): IncomingCallStarted[] {
    return [...this.calls].sort(
      (a, b) => this.startedAtMs(a) - this.startedAtMs(b)
    );
  }

  get callsByCategory(): Record<CallCategory, IncomingCallStarted[]> {
    const grouped: Record<CallCategory, IncomingCallStarted[]> = {
      Normal: [],
      Urgent: [],
      Driver: [],
    };
    for (const call of this.sortedCalls) {
      const category: CallCategory =
        call.category === "Urgent" || call.category === "Driver"
          ? call.category
          : "Normal";
      grouped[category].push(call);
    }
    return grouped;
  }

  get categoryCounts(): Record<CallCategory, number> {
    return {
      Normal: this.callsByCategory.Normal.length,
      Urgent: this.callsByCategory.Urgent.length,
      Driver: this.callsByCategory.Driver.length,
    };
  }

  get selectedCalls(): IncomingCallStarted[] {
    return this.callsByCategory[this.selectedCategory];
  }

  get oldestCall(): IncomingCallStarted | null {
    return this.selectedCalls.length > 0 ? this.selectedCalls[0] : null;
  }

  get restCalls(): IncomingCallStarted[] {
    return this.selectedCalls.slice(1);
  }

  get hasMoreCalls(): boolean {
    return this.selectedCalls.length > 1;
  }

  private startedAtMs(call: IncomingCallStarted): number {
    if (!call.startedAt) {
      return 0;
    }
    const ms = Date.parse(call.startedAt);
    return Number.isNaN(ms) ? 0 : ms;
  }

  mounted() {
    this.$dispatcherHub.$on(
      DispatcherHubEvent.IncomingCallStarted,
      this.onIncomingCallStarted
    );
    this.$dispatcherHub.$on(
      DispatcherHubEvent.CallRemovedFromQueue,
      this.onCallRemovedFromQueue
    );
    this.$dispatcherHub.$on(
      DispatcherHubEvent.IncomingCallEnded,
      this.onIncomingCallEnded
    );
    this.$dispatcherHub.$on(
      DispatcherHubEvent.CallAnswered,
      this.onCallAnswered
    );
    this.$dispatcherHub.$on("connected", this.onHubConnected);
    this.$dispatcherHub.$on("disconnected", this.onHubDisconnected);

    if (this.$dispatcherHub.connected) {
      this.onHubConnected();
    }
  }

  beforeDestroy() {
    this.$dispatcherHub.$off(
      DispatcherHubEvent.IncomingCallStarted,
      this.onIncomingCallStarted
    );
    this.$dispatcherHub.$off(
      DispatcherHubEvent.CallRemovedFromQueue,
      this.onCallRemovedFromQueue
    );
    this.$dispatcherHub.$off(
      DispatcherHubEvent.IncomingCallEnded,
      this.onIncomingCallEnded
    );
    this.$dispatcherHub.$off(
      DispatcherHubEvent.CallAnswered,
      this.onCallAnswered
    );
    this.$dispatcherHub.$off("connected", this.onHubConnected);
    this.$dispatcherHub.$off("disconnected", this.onHubDisconnected);
  }

  onHubConnected() {
    this.isConnected = true;
    this.seedActiveCalls();
    refreshMyActiveCall();
  }

  onHubDisconnected() {
    this.isConnected = false;
    this.calls = [];
    this.isExpanded = false;
  }

  async seedActiveCalls() {
    this.seeding = true;
    this.pendingEvents = [];
    try {
      const response = await this.callsApi.getActiveCalls().catch(() => null);

      if (!response || !this.isConnected) {
        return;
      }

      let next = response.data.filter((c) => !!(c && c.callId));

      for (const ev of this.pendingEvents) {
        if (ev.kind === "start") {
          if (ev.call && !next.some((c) => c.callId === ev.callId)) {
            next.push(ev.call);
          }
        } else if (ev.kind === "end" || ev.kind === "removeFromQueue") {
          next = next.filter((c) => c.callId !== ev.callId);
        }
      }

      this.calls = next;
    } finally {
      this.seeding = false;
      this.pendingEvents = [];
    }
  }

  onIncomingCallStarted(call: IncomingCallStarted) {
    if (!this.isConnected || !call || !call.callId) {
      return;
    }

    if (this.callState(call.callId).queueClosed) {
      return;
    }

    if (this.seeding) {
      this.pendingEvents.push({ kind: "start", callId: call.callId, call });
    }

    if (this.calls.some((c) => c.callId === call.callId)) {
      return;
    }

    this.calls.push(call);
  }

  onCallRemovedFromQueue(callId: string) {
    if (!this.isConnected || !callId) {
      return;
    }

    this.callState(callId).queueClosed = true;

    if (this.seeding) {
      this.pendingEvents.push({ kind: "removeFromQueue", callId });
    }

    this.removeCall(callId);
  }

  onIncomingCallEnded(call: IncomingCallEnded) {
    if (!this.isConnected || !call || !call.callId) {
      return;
    }

    const endedState = this.callState(call.callId);
    endedState.ended = true;
    endedState.queueClosed = true;

    if (this.seeding) {
      this.pendingEvents.push({ kind: "end", callId: call.callId });
    }

    this.removeCall(call.callId);

    if (this.activeCall?.callId === call.callId) {
      this.activeCall = null;
    }
  }

  removeCall(callId: string) {
    const index = this.calls.findIndex((c) => c.callId === callId);
    if (index !== -1) {
      this.calls.splice(index, 1);
    }
    if (!this.hasMoreCalls) {
      this.isExpanded = false;
    }
  }

  onCallAnswered(call: IncomingCallAnswered) {
    if (!this.isConnected || !call || !call.callId) {
      return;
    }

    if (this.callState(call.callId).ended) {
      return;
    }

    this.activeCall = call;
  }

  selectCategory(category: CallCategory) {
    if (this.selectedCategory !== category) {
      this.selectedCategory = category;
      this.isExpanded = false;
    }
  }

  toggleExpanded() {
    if (this.isExpanded) {
      this.isExpanded = false;
    } else if (this.hasMoreCalls) {
      this.isExpanded = true;
    }
  }

  collapse() {
    this.isExpanded = false;
  }

  async onTakeCall(call: IncomingCallStarted) {
    if (!call || !call.activityName) {
      return;
    }

    try {
      await this.callsApi.answerCall({ activityName: call.activityName });
    } catch {
      eventBus.$emit(
        Events.ShowSideAlert,
        new SideAlertModel("Hovor se nepodařilo přijmout.", "error", 4000)
      );
    }
  }

  onActiveRedirect() {
    if (this.activeCall) {
      this.onRedirectCall(this.activeCall);
    }
  }

  async onActiveHangup() {
    const activityName = this.activeCall?.activityName;
    if (!activityName) {
      return;
    }

    try {
      await this.callsApi.hangupCall({ activityName });
    } catch {
      eventBus.$emit(
        Events.ShowSideAlert,
        new SideAlertModel("Hovor se nepodařilo ukončit.", "error", 4000)
      );
    }
  }

  async onRedirectCall(call: IncomingCallStarted | IncomingCallAnswered) {
    if (!call || !call.activityName) {
      return;
    }

    this.redirectCallTarget = call;
    this.redirectTargets = [];
    this.redirectDialog = true;
    this.redirectLoading = true;

    try {
      const response = await this.callsApi.getRedirectTargets();
      this.redirectTargets = response.data.targets ?? [];
    } catch {
      this.redirectDialog = false;
      this.redirectCallTarget = null;
      eventBus.$emit(
        Events.ShowSideAlert,
        new SideAlertModel("Hovor se nepodařilo přesměrovat.", "error", 4000)
      );
    } finally {
      this.redirectLoading = false;
    }
  }

  async selectRedirectTarget(target: RedirectTargetModel) {
    const activityName = this.redirectCallTarget?.activityName;
    if (!activityName || !target.phoneNumber || this.redirectSubmitting) {
      return;
    }

    this.redirectSubmitting = true;

    try {
      await this.callsApi.redirectCall({
        activityName,
        targetPhoneNumber: target.phoneNumber,
      });
      this.closeRedirectDialog();
    } catch {
      eventBus.$emit(
        Events.ShowSideAlert,
        new SideAlertModel("Hovor se nepodařilo přesměrovat.", "error", 4000)
      );
    } finally {
      this.redirectSubmitting = false;
    }
  }

  closeRedirectDialog() {
    this.redirectDialog = false;
    this.redirectCallTarget = null;
    this.redirectTargets = [];
  }

  onShowDetail(call: IncomingCallStarted) {
    this.openCustomerProfile(call.customerId);
  }

  onQueueOrder(call: IncomingCallStarted) {
    this.openCustomerOrder(call.customerId);
  }

  onActiveDetail() {
    this.openCustomerProfile(this.activeCall?.customerId ?? null);
  }

  onActiveOrder() {
    this.openCustomerOrder(this.activeCall?.customerId ?? null);
  }

  private openCustomerProfile(customerId: number | null) {
    if (customerId == null) {
      return;
    }

    this.$router.push({
      name: "customer",
      params: { id: String(customerId) },
    });
  }

  private openCustomerOrder(customerId: number | null) {
    this.$router.push({
      name: "order",
      params: { id: "0" },
      query: customerId != null ? { customer: String(customerId) } : {},
    });
  }
}
